/**
 * Coins Store
 * Manages coin balances for all children in the household.
 * Per-child balances are stored in a Map; `currentBalance`/`balance` expose
 * the balance of the child currently in context (backward-compatible).
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { coinService } from '@/services/coin.service';
import { useAppContextStore } from './appContext';

export const useCoinsStore = defineStore('coins', () => {
  // Per-child balances (childId -> coins). Source of truth is children.coins.
  const balances = ref<Map<string, number>>(new Map());
  // Heure de la dernière lecture du solde en base, par enfant.
  const loadedAt = new Map<string, number>();
  const loading = ref(false);
  const error = ref<string | null>(null);

  function balanceFor(childId: string | null | undefined): number {
    if (!childId) return 0;
    return balances.value.get(childId) ?? 0;
  }

  function setBalance(childId: string, value: number): void {
    const next = new Map(balances.value);
    next.set(childId, value);
    balances.value = next;
  }

  // The balance of the child currently in context (child-view child wins, else current child).
  const currentBalance = computed(() => {
    const appContext = useAppContextStore();
    const id = appContext.childViewChildId ?? appContext.currentChildId ?? null;
    return balanceFor(id);
  });

  // Backward-compat alias for existing `.balance` consumers.
  const balance = currentBalance;

  // Computed
  const hasEnoughCoins = computed(() => (amount: number) => currentBalance.value >= amount);

  /**
   * Load coin balance for a single child (or the current child).
   * N'applique le résultat que si l'enfant courant est encore celui demandé (évite d'afficher
   * le solde d'un enfant précédent si une requête tardive revient après un changement d'enfant).
   */
  async function loadBalance(childId?: string, opts: { maxAgeMs?: number } = {}): Promise<void> {
    const appContext = useAppContextStore();
    const targetChildId = childId ?? appContext.currentChildId ?? null;

    if (!targetChildId) return;

    // Un lecteur qui ne fait que suivre un événement peut se contenter d'un
    // solde lu à l'instant : la validation qui a émis l'événement vient de le
    // relire elle-même (HARMONIA-77).
    if (opts.maxAgeMs && Date.now() - (loadedAt.get(targetChildId) ?? 0) < opts.maxAgeMs) return;

    loading.value = true;
    error.value = null;

    try {
      const coinBalance = await coinService.getCoinBalance(targetChildId);
      setBalance(targetChildId, coinBalance);
      loadedAt.set(targetChildId, Date.now());
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load coin balance';
      console.error('Error loading coin balance:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load coin balances for multiple children at once (household hydration).
   */
  async function loadBalancesForChildren(childIds: string[]): Promise<void> {
    if (!childIds.length) return;
    loading.value = true;
    error.value = null;
    try {
      const map = await coinService.getCoinBalancesForChildren(childIds);
      const next = new Map(balances.value);
      for (const [id, v] of map) next.set(id, v);
      balances.value = next;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load coin balances';
      console.error('Error loading coin balances:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Award coin to the current child
   * Updates the balance optimistically
   */
  async function awardCoin(
    childId?: string,
    source: string = 'task_completion',
    referenceId?: string
  ): Promise<number> {
    const appContext = useAppContextStore();
    const targetChildId = childId || appContext.currentChildId;

    if (!targetChildId) {
      throw new Error('No child selected');
    }

    loading.value = true;
    error.value = null;

    try {
      const newBalance = await coinService.awardCoin(targetChildId, source, referenceId);
      setBalance(targetChildId, newBalance);
      return newBalance;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to award coin';
      console.error('Error awarding coin:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Deduct coins from the current child
   * Updates the balance optimistically
   */
  async function deductCoin(
    amount: number,
    childId?: string,
    source: string = 'shop_purchase',
    referenceId?: string
  ): Promise<number> {
    const appContext = useAppContextStore();
    const targetChildId = childId || appContext.currentChildId;

    if (!targetChildId) {
      throw new Error('No child selected');
    }

    loading.value = true;
    error.value = null;

    try {
      const newBalance = await coinService.deductCoin(targetChildId, amount, source, referenceId);
      setBalance(targetChildId, newBalance);
      return newBalance;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to deduct coins';
      console.error('Error deducting coins:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Reset all balances (e.g., on logout)
   */
  function resetBalance(): void {
    balances.value = new Map();
    error.value = null;
  }

  return {
    // State
    balances,
    balance,
    currentBalance,
    loading,
    error,
    // Computed
    hasEnoughCoins,
    // Actions
    balanceFor,
    setBalance,
    loadBalance,
    loadBalancesForChildren,
    awardCoin,
    deductCoin,
    resetBalance,
  };
});
